import type { ReactNode } from 'react';
import { Button, CircularProgress, IconButton, Tooltip } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DownloadIcon from '@mui/icons-material/Download';
import InfoButton from '../../../components/InfoButton';
import { DASHBOARD_LABELS as LABELS } from '../../../utils/labels';

interface StatDayCardProps {
  icon: ReactNode;
  title: string;
  infoDescription: string;
  todayValue: number;
  tomorrowValue: number;
  warn?: boolean;
  onNavigate: () => void;
  onDownload?: () => void;
  downloadLoading?: boolean;
  downloadLabel?: string;
  testId: string;
}

export default function StatDayCard({
  icon,
  title,
  infoDescription,
  todayValue,
  tomorrowValue,
  warn,
  onNavigate,
  onDownload,
  downloadLoading,
  downloadLabel,
  testId,
}: StatDayCardProps) {
  return (
    <div className="bg-surface border rounded-xl p-5 flex flex-col gap-4 border-border" data-testid={testId}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-0.5">
          <h2 className="text-base flex items-center gap-2 font-semibold">
            {icon}
            {title}
          </h2>
          <InfoButton title={title} description={infoDescription} />
        </div>
        {onDownload && (
          <Tooltip title={downloadLabel ?? ''}>
            <span>
              <IconButton
                onClick={onDownload}
                disabled={downloadLoading}
                aria-label={downloadLabel}
                data-testid="download-button"
              >
                {downloadLoading ? (
                  <CircularProgress size="1rem" />
                ) : (
                  <DownloadIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        )}
      </div>

      <div className="space-y-2.5">
        <div className="flex items-baseline justify-between gap-4">
          <span className="text-text-secondary">{LABELS.labelToday}</span>
          <span
            className={`text-2xl font-bold tabular-nums ${
              warn && todayValue > 0 ? 'text-error' : ''
            }`}
            data-testid="stat-value-today"
          >
            {todayValue}
          </span>
        </div>
        <div className="flex items-baseline justify-between gap-4">
          <span className="text-text-secondary">{LABELS.labelTomorrow}</span>
          <span
            className={`text-2xl font-bold tabular-nums ${
              warn && tomorrowValue > 0 ? 'text-error' : ''
            }`}
            data-testid="stat-value-tomorrow"
          >
            {tomorrowValue}
          </span>
        </div>
      </div>

      <Button
        variant="text"
        size="small"
        endIcon={<ArrowForwardIcon fontSize="small" />}
        onClick={onNavigate}
        className="self-start -ml-1 text-xs"
        data-testid="view-in-planning-button"
      >
        {LABELS.linkToPlanning}
      </Button>
    </div>
  );
}
